import NumInput from './NumInput';
import { smartParseTol } from './smartParseTol';
import { fmt, fmtSigned } from './numFormat';
import { getDeviations } from '../../../utils/elementShape';
import { parseNormKurzform } from './normKurzform';
import { toast } from '../../../lib/toast';

// Free darf wie im Norm-Menü nur ISO 2768-m (siehe NormCell FREE_ALLOWED).
const FREE_NORMEN = new Set(['iso2768m']);

/**
 * Schnellwahl (C6): Ist der Text eine Toleranzklasse (h7, H7, m …), wird
 * die Norm gesetzt bzw. ein Hinweis gezeigt. Liefert für NumInput
 * 'reset' (übernommen, Feld zurücksetzen, Enter darf weiter),
 * 'reject' (nicht übernommen, Feld zurücksetzen, Enter legt keine Zeile
 * an) oder null (keine Klasse, als Zahl auswerten).
 */
function handleKurzform(raw, { isFree, onCommit }) {
  const k = parseNormKurzform(raw);
  if (!k) return null;
  if (k.passung) {
    toast.info(`Passung ${k.passung.bohrung}/${k.passung.welle}: Bohrung (${k.passung.bohrung}) und Welle (${k.passung.welle}) bitte in ihren eigenen Zeilen eintragen.`);
    return 'reject';
  }
  if (k.unbekannt) {
    toast.info(`Toleranzklasse „${k.unbekannt}“ gibt es nicht.`);
    return 'reject';
  }
  if (k.unverstanden) {
    toast.info(`„${k.unverstanden}“ ist weder Zahl noch Toleranzklasse. Beispiele: 0,1 oder h7 oder m.`);
    return 'reject';
  }
  if (isFree && !FREE_NORMEN.has(k.norm)) {
    toast.info(k.norm.startsWith('iso2768') ? 'ISO 2768 f/c/v ist im Pro-Plan.' : 'ISO 286 ist im Pro-Plan.');
    return 'reject';
  }
  onCommit({ norm: k.norm });
  if (k.formLage) {
    toast.info(`ISO 2768-${k.norm.slice(-1)} übernommen. ${k.formLage} (Form und Lage) wirkt nicht auf Längenmaße.`);
  }
  return 'reset';
}

// Tolerance editor cell. Drives both modes:
//   - symmetric  → one input that accepts smart-parsed text. Typing
//                  two numbers (e.g. "0,3-0,2") auto-flips the row to
//                  asymmetric on commit.
//   - asymmetric → two stacked inputs (upper above, lower below). Sign
//                  follows the typed value, so unidirektional pairs
//                  like -0,1 / -0,2 stay both-red.
//
// All commits go through `onCommit(patch)` where patch is a partial
// element to merge into the store. The store's updateElement is
// responsible for re-running refreshFromNorm when needed.

// Small NORM badge — only visible when the displayed tol values
// actually come from the row's norm formula (not overridden). When
// the user types a manual value the badge disappears + the NormCell
// turns dashed/grey, matching the "Norm vorgemerkt — überschrieben"
// contract logged in the user-feedback memo.
function NormBadge() {
  return (
    <span
      className="bg-transparent border border-brand text-brand"
      title="Toleranz aus Norm"
      style={{
        fontSize: 8, fontWeight: 800, letterSpacing: 0.5,
        padding: '1px 4px', borderRadius: 3, flexShrink: 0,
        fontFamily: 'var(--font-sans)', textTransform: 'uppercase',
        alignSelf: 'center',
      }}
    >
      Norm
    </span>
  );
}

export default function TolCell({ row, onCommit, onEnter, overridden, isFree = false }) {
  const isAsym = row.tolMode === 'asymmetric';
  const usingNorm = !!row.norm;
  const showNormBadge = usingNorm && !overridden;
  const { lower, upper } = getDeviations(row);

  // Switch between symmetric ↔ asymmetric. Carries the current values
  // through so the transition isn't destructive — switching to asym
  // pre-fills both deviations from the current sym tol, and back
  // collapses two equal-magnitude opposing values into one ± value.
  const toggleMode = () => {
    if (isAsym) {
      // Asymmetric → symmetric: take the larger magnitude as ± value.
      const t = Math.max(Math.abs(row.tolUpper || 0), Math.abs(row.tolLower || 0));
      onCommit({ tolMode: 'symmetric', tol: t, tolUpper: 0, tolLower: 0 });
    } else {
      const t = Math.abs(row.tol || 0);
      onCommit({ tolMode: 'asymmetric', tol: 0, tolUpper: t, tolLower: -t });
    }
  };

  // Symmetric mode — single ± input with smart-parse on commit.
  if (!isAsym) {
    const commitSym = (raw) => {
      const k = handleKurzform(raw, { isFree, onCommit });
      if (k) return k;
      const parsed = smartParseTol(raw);
      if (!parsed) return;
      // If user typed a two-number expression that flipped us to
      // asymmetric, also clear the row's norm — manual override wins.
      // For a plain symmetric value we leave norm alone; the store's
      // refreshFromNorm decides whether the typed value sticks or
      // gets overridden by the norm.
      const patch = { ...parsed };
      if (parsed.tolMode === 'asymmetric' && row.norm) patch.norm = '';
      onCommit(patch);
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', height: '100%' }}>
        <button
          type="button"
          onClick={toggleMode}
          tabIndex={-1}
          title="Auf asymmetrisch umschalten"
          className="bg-surface-2 border border-line text-ink-3 hover:text-brand"
          style={{
            width: 24, height: 26, fontSize: 12, fontWeight: 700,
            borderRadius: 5, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontFamily: 'var(--font-mono)',
          }}
        >
          ±
        </button>
        <div style={{ flex: 1, position: 'relative' }}>
          <NumInput
            value={row.tol}
            onCommit={commitSym}
            onEnter={onEnter}
            placeholder={usingNorm ? fmt(Math.abs(upper), 3) : '0,000'}
            ghost={usingNorm && !row.tol}
            align="right"
            ariaLabel="Toleranz (symmetrisch)"
            title="Toleranz, z. B. 0,1 oder 0,3 −0,2, oder Klasse wie h7, H7, m"
          />
        </div>
        {showNormBadge && <NormBadge />}
      </div>
    );
  }

  // Asymmetric mode — upper / lower stacked. Empty inputs fall back to
  // norm-derived ghost values so the user sees what the norm provides.
  const commitField = (field) => (raw) => {
    const k = handleKurzform(raw, { isFree, onCommit });
    if (k) return k;
    // Strip everything that isn't a signed decimal.
    const m = String(raw).trim().match(/^([+-]?\d+(?:[.,]\d+)?)/);
    const n = m ? parseFloat(m[1].replace(',', '.')) : 0;
    const next = { [field]: Number.isFinite(n) ? n : 0 };
    // Same return-to-symmetric guard as smartParseTol: if upper and
    // lower mirror (u = -l, u > 0) after this commit, collapse back.
    const u = field === 'tolUpper' ? next.tolUpper : (row.tolUpper || 0);
    const l = field === 'tolLower' ? next.tolLower : (row.tolLower || 0);
    if (Math.abs(u + l) < 1e-9 && u > 0) {
      onCommit({ tolMode: 'symmetric', tol: u, tolUpper: 0, tolLower: 0 });
    } else {
      onCommit(next);
    }
  };

  const upperColor = (row.tolUpper || 0) >= 0 ? 'var(--color-ok)' : 'var(--color-err)';
  const lowerColor = (row.tolLower || 0) >= 0 ? 'var(--color-ok)' : 'var(--color-err)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', height: '100%' }}>
      <button
        type="button"
        onClick={toggleMode}
        tabIndex={-1}
        title="Auf symmetrisch umschalten"
        className="bg-surface-2 border border-line text-brand"
        style={{
          width: 24, height: 32, borderRadius: 5, cursor: 'pointer',
          fontSize: 9, fontWeight: 700,
          display: 'inline-flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          fontFamily: 'var(--font-mono)', lineHeight: 1,
        }}
      >
        <span style={{ color: 'var(--color-ok)' }}>+</span>
        <span style={{ color: 'var(--color-err)' }}>−</span>
      </button>
      {/* Flex-distribute the two inputs so the stack centres + scales
          with --row-h. Fixed 16 px lines spilled the lower input outside
          the cell in Kompakt density (--row-h: 34 px); flex:1 splits the
          space evenly and alignItems:center keeps each line vertically
          centred within its half. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', height: '100%', minHeight: 0 }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', width: '100%' }}>
          <NumInput
            value={row.tolUpper}
            onCommit={commitField('tolUpper')}
            placeholder={usingNorm ? fmtSigned(upper, 3) : ''}
            ghost={usingNorm && (row.tolUpper === '' || row.tolUpper == null)}
            align="right"
            ariaLabel="Obere Toleranzgrenze"
            title="Abmaß, z. B. +0,1, oder Klasse wie h7, H7, m"
            style={{ color: upperColor, fontSize: 11.5, fontWeight: 600, lineHeight: 1 }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', width: '100%' }}>
          <NumInput
            value={row.tolLower}
            onCommit={commitField('tolLower')}
            onEnter={onEnter}
            placeholder={usingNorm ? fmtSigned(lower, 3) : ''}
            ghost={usingNorm && (row.tolLower === '' || row.tolLower == null)}
            align="right"
            ariaLabel="Untere Toleranzgrenze"
            title="Abmaß, z. B. +0,1, oder Klasse wie h7, H7, m"
            style={{ color: lowerColor, fontSize: 11.5, fontWeight: 600, lineHeight: 1 }}
          />
        </div>
      </div>
      {showNormBadge && <NormBadge />}
    </div>
  );
}
